import { omit } from "lodash"
import type { Scalar } from "helpers/types/helpers"

const NON_DIFFERENTIATING = new Set(["name", "isOptimization"])

export function generateQueryKey(queryType: string, params: Record<string, unknown> = {}): string[] {
  const parts: string[] = []
  for (const key of Object.keys(params).sort(dateRangeLast)) {
    const val = params[key]
    if (val == null || val === false || val === "") continue
    if (typeof val === "object") {
      parts.push(`${key}:${stableSerialize(val)}`)
    } else {
      parts.push(val === true ? key : `${key}:${val}`)
    }
  }
  return [queryType, ...parts]
}

// Returns both the full prefixed key (used as TanStack queryKey and for invalidation) and the
// prefix-free graphqlKey (used for stable cache identification, independent of which gatherer
// prefix originated the request).
export function generateGathererKeys(queryType: string, params: Record<string, unknown> = {}): { key: string[], graphqlKey: string[] } {
  return {
    key: generateQueryKey(queryType, params),
    graphqlKey: generateQueryKey(queryType, omit(params, "prefix")),
  }
}

export function stableCacheKey(queryKeys: unknown[][]): string {
  return queryKeys.map(key => (key as Scalar[]).map(String).join("|")).join("::")
}

// Sorts keys alphabetically, but keeps `from` and `to` adjacent at the end
// so date-range bounds are always visually grouped in query keys.
const DATE_RANGE_ORDER: Record<string, number> = { from: 1, to: 2 }
function dateRangeLast(a: string, b: string): number {
  const aRank = DATE_RANGE_ORDER[a] ?? 0
  const bRank = DATE_RANGE_ORDER[b] ?? 0
  if (aRank !== bRank) return aRank - bRank
  return a.localeCompare(b)
}

// Strip NON_DIFFERENTIATING fields from any object that has an `id` field — those are
// identity-keyed objects (positionSet, base, betaInstrument…) where `name` is a display
// label, not a cache discriminator. Objects without `id` (e.g. rulesConfiguration) keep
// all fields so semantically meaningful `name` values are never silently dropped.
function stableSerialize(value: unknown): string {
  if (value === null || value === undefined) return ""
  if (typeof value !== "object") return String(value)
  if (Array.isArray(value)) return `[${value.filter(v => v != null).map(stableSerialize).join("~")}]`
  const obj = value as Record<string, unknown>
  const hasId = "id" in obj
  const entries = Object.entries(obj)
    .filter(([k, v]) => v != null && !(hasId && NON_DIFFERENTIATING.has(k)))
    .sort(([a], [b]) => a.localeCompare(b))
  return `{${entries.map(([k, v]) => `${k}:${stableSerialize(v)}`).join("~")}}`
}
